import type { Database } from '@/types/supabase';
import type { DrawOptions } from '@/draw/types';
import { buildSeatPlan, type SeatPlanEntry } from '@/draw/utils/seatPlan';
import { resolveAdvancementTiers } from '@/draw/utils/advancementTiers';
type DrawMatch = Database['public']['Tables']['draw_match']['Row'];
type Schedule = Database['public']['Tables']['event_match_schedule']['Row'];
export interface PoolCorrectionSnapshot {
  poolPlayerIds: string[];
  matches: DrawMatch[];
  schedules: Schedule[];
  eventId?: string;
}
function blocked(message: string): never {
  throw Object.assign(new Error(message), { status: 400 });
}
/** A linked scorer counts as started even while its draw row still says ready. */
export function assertUnstartedAdvancementTarget(match: DrawMatch, schedule?: Schedule) {
  if (
    !['pending', 'ready'].includes(match.status) ||
    match.match_id ||
    match.winner_id ||
    match.loser_id ||
    match.outcome ||
    match.score != null ||
    schedule?.match_id ||
    (schedule && (!['scheduled', 'pending', 'ready'].includes(schedule.status) || schedule.outcome))
  ) {
    blocked(
      'A pool advancement match has already started or completed. Unwind it before changing this pool result.',
    );
  }
  if (match.event_match_schedule_id && !schedule)
    blocked('Could not verify a pool advancement schedule.');
  if (
    schedule &&
    (schedule.player_1_id !== match.player1_id || schedule.player_2_id !== match.player2_id)
  ) {
    blocked(
      'Pool advancement draw and schedule participants disagree. Reconcile them before changing this pool result.',
    );
  }
}
/** Plan only persisted pool-member seats. Opposite-pool identities and BYE flags are retained. */
export function planPoolAdvancementUndo(snapshot: PoolCorrectionSnapshot) {
  const members = new Set(snapshot.poolPlayerIds);
  return snapshot.matches.flatMap((match) => {
    const clear1 = !!match.player1_id && members.has(match.player1_id);
    const clear2 = !!match.player2_id && members.has(match.player2_id);
    if (!clear1 && !clear2) return [];
    const schedule = snapshot.schedules.find((s) => s.id === match.event_match_schedule_id);
    assertUnstartedAdvancementTarget(match, schedule);
    if (schedule && snapshot.eventId && schedule.event_id !== snapshot.eventId)
      blocked('Pool advancement schedule belongs to another event.');
    const player1Id = clear1 ? null : match.player1_id;
    const player2Id = clear2 ? null : match.player2_id;
    return [
      {
        match,
        schedule,
        player1Id,
        player2Id,
        status: player1Id && player2Id ? ('ready' as const) : ('pending' as const),
      },
    ];
  });
}

/** Resolve inferred dependencies without recalculating or changing tournament standings. */
export function poolCorrectionSnapshot(
  source: DrawMatch,
  stages: Database['public']['Tables']['draw_stage']['Row'][],
  matches: DrawMatch[],
  schedules: Schedule[],
  draw: Database['public']['Tables']['draw']['Row'],
): PoolCorrectionSnapshot | null {
  const stage = stages.find((s) => s.id === source.stage_id);
  if (!stage) blocked('Could not verify the source draw stage.');
  if (stage.type !== 'round_robin' || stage.context !== 'qualifying') return null;
  const poolPlayerIds = [
    ...new Set(
      matches
        .filter((m) => m.stage_id === stage.id)
        .flatMap((m) => [m.player1_id, m.player2_id])
        .filter((id): id is string => !!id),
    ),
  ];
  const targets = matches.filter((m) =>
    stages.some((s) => s.id === m.stage_id && s.type === 'elimination'),
  );
  const result = { poolPlayerIds, matches: targets, schedules, eventId: draw.event_id };
  // Verify configured destinations too: a foreign/stale occupant must never be replaced
  // just because none of this pool's members are currently found in the bracket.
  const pool = Number(stage.stage_id.match(/pool_(\d+)/)?.[1]);
  if (!pool) blocked('Could not verify the qualifying pool number.');
  const options = ((draw.settings as { options?: DrawOptions } | null)?.options ??
    {}) as DrawOptions;
  for (const targetStage of stages.filter((s) => s.type === 'elimination')) {
    const settings = targetStage.settings as {
      seatPlan?: SeatPlanEntry[];
      ladderTemplateId?: string;
    } | null;
    // Legacy ladder placement is not inferred by standard pool seat plans. Refuse
    // corrections once populated rather than apply an incomplete standard undo.
    if (
      settings?.ladderTemplateId &&
      targets.some((m) => m.stage_id === targetStage.id && (m.player1_id || m.player2_id))
    ) {
      blocked(
        'This pool feeds a placement template. Clear its downstream placement before correcting the pool.',
      );
    }
    const first = targets
      .filter((m) => m.stage_id === targetStage.id && m.round_number === 1)
      .sort((a, b) => a.match_number - b.match_number);
    let plan = settings?.seatPlan;
    if (!plan && options.qualifyingPools && first.length) {
      const tier = resolveAdvancementTiers(options).find((t) => t.stageId === targetStage.stage_id);
      if (tier)
        plan = buildSeatPlan(
          tier,
          options.qualifyingPools.numPools,
          first.length * 2,
          tier.index === 1 ? (options.seededPlayersCount ?? 0) : 0,
        );
    }
    for (const seat of plan ?? []) {
      if (seat.pool !== pool) continue;
      const target = first[seat.matchIndex];
      if (!target) blocked('Could not verify a configured pool advancement seat.');
      const occupant = target[`${seat.position}_id`];
      if (occupant && !poolPlayerIds.includes(occupant))
        blocked(
          'A configured pool advancement seat has a different participant. Reconcile it before correcting this pool.',
        );
      const schedule = schedules.find((s) => s.id === target.event_match_schedule_id);
      if (schedule && schedule.event_id !== draw.event_id) {
        blocked('Pool advancement schedule belongs to another event.');
      }
      assertUnstartedAdvancementTarget(target, schedule);
    }
  }
  return result;
}
